import { Dialog } from '@angular/cdk/dialog';
import { SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  InputSignal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@core/authentication/auth.service';
import { OiqFormCheckboxComponent } from '@oiq-ui-sdk/form';
import { OiqApplicationAddDialog } from '@v2/modules/applications/components/add/add-application.service';
import { OiqApplicationAddStore } from '@v2/modules/applications/components/add/add-application.store';
import { EmailLinkTarget } from '@v2/modules/email-app/email-app.interfaces';
import { EMAIL_APP_STORE } from '@v2/modules/email-app/email-app.store';
import { emailLinkTarget } from '@v2/modules/email-app/email-app.utils';
import { EmailNewApplicationService } from '@v2/modules/email-app/services/email-new-application.service';
import { Email } from '@v2/modules/email-app/services/email-oauth.service';
import { OiqAttachmentsUploadStore } from '@v2shared/components/attachments/attachments-upload.store';
import { finalize } from 'rxjs/operators';
import { SelectClientDialogComponent } from '../select-client-dialog/select-client-dialog.component';

@Component({
  selector: 'oiq-email-list-item, a[oiq-email-list-item]',
  imports: [OiqFormCheckboxComponent, SlicePipe],
  templateUrl: './email-list-item.component.html',
  styleUrl: './email-list-item.component.scss',
  providers: [
    { provide: OiqAttachmentsUploadStore, useFactory: () => new OiqAttachmentsUploadStore() },
    { provide: OiqApplicationAddStore, useFactory: () => new OiqApplicationAddStore() },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailListItemComponent {
  private dialog: Dialog = inject(Dialog);
  private store = inject(EMAIL_APP_STORE);
  private authService = inject(AuthService);
  private applicationDialog = inject(OiqApplicationAddDialog);
  private applicationStore = inject(OiqApplicationAddStore);
  private attachmentsUploadStore = inject(OiqAttachmentsUploadStore);
  private newApplicationFlow = inject(EmailNewApplicationService);
  private destroyRef = inject(DestroyRef);

  email: InputSignal<Email> = input.required<Email>();

  isActiveSentLabel = this.store.isActiveSentLabel;

  private isPreparingNewApplication = signal<boolean>(false);

  appDialogsDisabled = computed(() => this.isPreparingNewApplication());

  // Ingestion errors reported by the backend for this email (keyed by email ID).
  errors = computed(() => this.store.getEmailErrors(this.email().ID));
  hasErrors = computed(() => this.errors().length > 0);

  // Computed signal for formatted date
  formattedDate = computed(() => {
    const emailDate = this.email().Date;
    if (!emailDate) return '';

    return this.formatEmailDate(emailDate);
  });

  // Computed signal for sender name
  senderName = computed(() => {
    const from = this.email().From;
    return this.parseSenderName(from);
  });

  // Computed signal for sender email address
  senderEmail = computed(() => {
    const from = this.email().From;
    return this.parseSenderEmail(from);
  });

  // Computed signal for avatar initials
  avatarInitials = computed(() => {
    const name = this.senderName();
    return this.getInitials(name);
  });

  // Computed signal for avatar background color
  avatarColor = computed(() => {
    const initials = this.avatarInitials();
    return this.getColorForInitial(initials.charAt(0));
  });

  // Computed signal for email association (client/application link)
  linked = computed(() => this.store.getEmailAssociation(emailLinkTarget(this.email())));

  // Thread metadata for the row's email — null if loaded list has no thread match
  // thread = computed(() => this.store.getThreadByEmailId(this.email().ThreadID || this.email().ID));

  // messageCount = computed(() => this.thread()?.MessageCount ?? 1);

  // isThread = computed(() => this.messageCount() > 1);

  // Computed signal for email to field
  recipients = computed(() => {
    const to = this.email().To;
    if (!to || to.length === 0) return '';
    return to.map(recipient => this.parseSenderName(recipient)).join(', ');
  });

  private parseSenderName(from: string): string {
    // Format: "Name <email@example.com>" or just "email@example.com"
    const match = from.match(/^(.+?)\s*<(.+?)>$/);

    if (match) {
      // Has name part, return it trimmed
      return match[1].trim();
    }

    // No name part, return the email address
    return from.trim();
  }

  private parseSenderEmail(from: string): string {
    // Format: "Name <email@example.com>" or just "email@example.com"
    const match = from.match(/^(.+?)\s*<(.+?)>$/);

    if (match) {
      // Has email in angle brackets
      return match[2].trim();
    }

    // Just the email address
    return from.trim();
  }

  private getInitials(name: string): string {
    // If it's an email address (contains @), extract from email part
    if (name.includes('@')) {
      const namePart = name.split('@')[0];
      const parts = namePart.split(/[._-]/);

      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
      } else {
        return namePart.substring(0, 2).toUpperCase();
      }
    }

    // It's a name - remove parentheses and content inside them
    const cleanName = name.replace(/\s*\([^)]*\)/g, '').trim();
    const parts = cleanName.split(/\s+/);

    if (parts.length >= 2) {
      // Take first and second letter of first two parts
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    } else if (parts.length === 1 && parts[0].length >= 2) {
      // Single word, take first letter
      return parts[0].substring(0, 1).toUpperCase();
    } else if (parts.length === 1 && parts[0].length === 1) {
      // Single letter
      return parts[0].toUpperCase();
    }

    return 'NA';
  }

  private getColorForInitial(letter: string): string {
    // Define colors for each letter A-Z
    const colorMap: { [key: string]: string } = {
      A: '#FF6B6B', // Red
      B: '#4ECDC4', // Teal
      C: '#45B7D1', // Blue
      D: '#FFA07A', // Light Salmon
      E: '#98D8C8', // Mint
      F: '#F7DC6F', // Yellow
      G: '#BB8FCE', // Purple
      H: '#85C1E2', // Sky Blue
      I: '#F8B739', // Orange
      J: '#52B788', // Green
      K: '#E63946', // Crimson
      L: '#A8DADC', // Powder Blue
      M: '#457B9D', // Steel Blue
      N: '#F4A261', // Sandy Brown
      O: '#2A9D8F', // Persian Green
      P: '#E76F51', // Burnt Sienna
      Q: '#8E7CC3', // Lavender
      R: '#6A994E', // Fern Green
      S: '#BC4749', // Bittersweet
      T: '#4A90E2', // Cornflower Blue
      U: '#F39C12', // Orange Peel
      V: '#9B59B6', // Amethyst
      W: '#16A085', // Green Sea
      X: '#E74C3C', // Alizarin
      Y: '#F1C40F', // Sun Flower
      Z: '#34495E', // Wet Asphalt
    };

    const upperLetter = letter.toUpperCase();
    return colorMap[upperLetter] || '#95A5A6'; // Default gray if not A-Z
  }

  private formatEmailDate(dateString: string): string {
    const emailDate = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const emailDateOnly = new Date(
      emailDate.getFullYear(),
      emailDate.getMonth(),
      emailDate.getDate()
    );

    // Format time as HH:MM AM/PM
    const timeString = emailDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    // Check if it's today
    if (emailDateOnly.getTime() === today.getTime()) {
      return `${timeString} Today`;
    }

    // Check if it's yesterday
    if (emailDateOnly.getTime() === yesterday.getTime()) {
      return `${timeString} Yesterday`;
    }

    // Check if it's within the current week
    const daysDiff = Math.floor(
      (today.getTime() - emailDateOnly.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysDiff <= 7 && daysDiff > 0) {
      const dayName = emailDate.toLocaleDateString('en-US', { weekday: 'long' });
      return `${timeString} ${dayName}`;
    }

    // For older dates, show date
    const formattedDate = emailDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    // If it's from a different year, include the year
    if (emailDate.getFullYear() !== now.getFullYear()) {
      return `${timeString} ${formattedDate}, ${emailDate.getFullYear()}`;
    }

    return `${timeString} ${formattedDate}`;
  }

  onSearchClients() {
    const dialogRef = this.dialog.open(SelectClientDialogComponent, {
      width: '1024px',
      autoFocus: false,
      restoreFocus: true,
      panelClass: ['email-documents-dialog-left'],
      data: {
        whiteLabels: [],
        whiteLabelId: '',
        whiteLabelName: 'this.whiteLabelName',
        action: 'search',
        mode: 'linkClient',
        appDialogsDisabled: this.appDialogsDisabled,
      },
    });

    dialogRef.closed.subscribe((result: any) => {
      if (!result) return;

      if (result?.openNewApplicationDialog) {
        this.onNewApplication();
        return;
      }

      const client = result.client;
      const application = result.application;

      if (client && client.id) {
        const clientId = typeof client.id === 'string' ? parseInt(client.id, 10) : client.id;
        const applicationId = application?.id
          ? typeof application.id === 'string'
            ? parseInt(application.id, 10)
            : application.id
          : undefined;

        // Link thread to client and optionally application via store
        this.store
          .linkEmailToClientAndApplication(emailLinkTarget(this.email()), clientId, applicationId)
          .subscribe();
      }
    });
  }

  onNewApplication() {
    if (this.isPreparingNewApplication()) return;

    const linkTarget = emailLinkTarget(this.email());
    const clientId = this.linked()?.clientId;

    this.isPreparingNewApplication.set(true);
    this.newApplicationFlow
      .collectThreadAttachments(this.email())
      .pipe(
        finalize(() => this.isPreparingNewApplication.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(attachments => {
        if (!attachments) return;

        if (attachments.length === 0) {
          this.openNewApplicationWithoutAttachments(linkTarget, clientId);
          return;
        }

        this.newApplicationFlow.openDocumentsDialog(attachments, {
          emailId: this.email().ID,
          linkTarget,
          clientId,
        });
      });
  }

  private openNewApplicationWithoutAttachments(
    linkTarget: EmailLinkTarget,
    clientId?: number | string
  ) {
    this.attachmentsUploadStore.reset();
    this.attachmentsUploadStore.tenantId = this.authService.getUser().tenantId;
    this.attachmentsUploadStore.type = 'application';

    this.applicationDialog
      .open({
        mode: 'upload',
        applicationStore: this.applicationStore,
        attachmentsStore: this.attachmentsUploadStore,
        formData: { clientId },
      })
      .closed.subscribe((result: any) => {
        if (result?.action === 'create') {
          this.store
            .linkEmailToClientAndApplication(linkTarget, result.data.clientId, result.data.id)
            .subscribe();
        }
      });
  }
}
